import React, { useEffect, useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography, Alert } from "@mui/material";
import { AuthContext } from "../context/AuthContext";

// ── Design tokens ─────────────────────────────────────────────────────────────
const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

function OAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signIn } = useContext(AuthContext);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const processOAuthCallback = async () => {
      // Check for errors first
      const errorParam = searchParams.get("error");
      if (errorParam) {
        setError(decodeURIComponent(errorParam));
        setProcessing(false);
        return;
      }

      // Extract user data from URL params
      const token = searchParams.get("token");
      const userId = searchParams.get("userId");
      const username = searchParams.get("username");
      const email = searchParams.get("email");
      const roles = searchParams.get("roles");
      const isNewUser = searchParams.get("isNewUser") === "true";
      const avatarUrl = searchParams.get("avatarUrl");
      const bannerUrl = searchParams.get("bannerUrl");
      const botAccount = searchParams.get("botAccount") === "true";
      const currentTermsVersion = searchParams.get("currentTermsVersion");
      const acceptedTermsVersion = searchParams.get("acceptedTermsVersion");
      const requiresTermsAcceptance =
        searchParams.get("requiresTermsAcceptance") === "true";

      if (!token || !userId || !username) {
        setError("Missing authentication data. Please try again.");
        setProcessing(false);
        return;
      }

      try {
        // Build user data object matching the format from AuthContext
        const userData = {
          token: token,
          id: parseInt(userId, 10),
          username: decodeURIComponent(username),
          email: email ? decodeURIComponent(email) : "",
          avatarUrl: avatarUrl ? decodeURIComponent(avatarUrl) : null,
          bannerUrl: bannerUrl ? decodeURIComponent(bannerUrl) : null,
          roles: roles ? decodeURIComponent(roles).split(",") : ["ROLE_USER"],
          tokenType: "Bearer",
          botAccount,
          currentTermsVersion: currentTermsVersion
            ? decodeURIComponent(currentTermsVersion)
            : null,
          termsAcceptedVersion: acceptedTermsVersion
            ? decodeURIComponent(acceptedTermsVersion)
            : null,
          requiresTermsAcceptance,
        };

        // Sign in the user
        signIn(userData);

        // Navigate based on whether this is a new user
        if (requiresTermsAcceptance) {
          navigate("/terms?required=true");
        } else if (isNewUser) {
          // New OAuth users go to profile photo setup with flag to choose username
          navigate("/add-new-photos?oauthSignup=true");
        } else {
          // Existing users go to last visited route or home
          let lastVisitedRoute = localStorage.getItem("lastVisitedRoute") || "/";
          if (lastVisitedRoute === "/signin" || lastVisitedRoute === "/oauth/callback") {
            lastVisitedRoute = "/";
          }
          navigate(lastVisitedRoute);
        }
      } catch (err) {
        console.error("OAuth callback error:", err);
        setError("Failed to complete sign in. Please try again.");
        setProcessing(false);
      }
    };

    processOAuthCallback();
  }, [searchParams, signIn, navigate]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.06) 0%, ${DL.bgDark} 60%)`,
        px: 2,
      }}
    >
      {processing && !error ? (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress
            size={48}
            thickness={3}
            sx={{ color: DL.green, mb: 3 }}
          />
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              color: DL.textSecondary,
              letterSpacing: "0.01em",
            }}
          >
            Completing sign in...
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: DL.textMuted,
              mt: 0.75,
            }}
          >
            You'll be redirected shortly.
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: "center", maxWidth: 400, width: "100%" }}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: "8px",
              backgroundColor: "rgba(255,100,103,0.1)",
              color: DL.red,
              border: "1px solid rgba(255,100,103,0.25)",
              "& .MuiAlert-icon": { color: DL.red },
              textAlign: "left",
            }}
          >
            {error}
          </Alert>
          <Typography
            onClick={() => navigate("/signin")}
            sx={{
              fontSize: "0.875rem",
              color: DL.textMuted,
              cursor: "pointer",
              "&:hover": { color: DL.green },
              transition: "color 0.2s ease",
            }}
          >
            ← Return to Sign In
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

export default OAuthCallbackPage;
